import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Network, Loader2, CheckCircle2, Building2, GraduationCap, Users } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import api from "@/lib/apiClient";
import { toast } from "sonner";

const TYPES = [
  { icon: Building2, t: "Home Care Agencies", d: "List your agency and access shared caregiver supply." },
  { icon: GraduationCap, t: "Training Providers", d: "Publish accredited courses to the T5 Academy." },
  { icon: Users, t: "Employers & Nonprofits", d: "Bring T5 workforce and care benefits to your people." },
];

export default function Partners() {
  const navigate = useNavigate();
  const [form, setForm] = useState({ organization: "", contact_name: "", email: "", phone: "", partner_type: "agency", message: "" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault(); setLoading(true);
    try { await api.post("/partner-applications", form); setDone(true); }
    catch { toast.error("Could not submit. Please try again."); }
    finally { setLoading(false); }
  };

  if (done) return (
    <div className="max-w-xl mx-auto px-6 py-24 text-center">
      <CheckCircle2 className="w-16 h-16 text-teal-600 mx-auto mb-5" />
      <h1 className="font-display text-3xl font-bold text-blue-950">Application received!</h1>
      <p className="mt-3 text-slate-600">Our partnerships team will review and reach out about joining the T5 platform.</p>
      <Button onClick={() => navigate("/")} className="mt-7 bg-blue-950 text-white h-12 px-7">Back to home</Button>
    </div>
  );

  return (
    <div>
      <section className="bg-blue-950 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <p className="text-xs uppercase tracking-[0.16em] text-teal-400 font-semibold mb-3 flex items-center gap-2"><Network className="w-4 h-4" /> Partner With T5</p>
          <h1 className="font-display text-4xl lg:text-5xl font-bold max-w-2xl leading-tight">Join the platform connecting the care economy.</h1>
          <p className="mt-4 text-blue-100/80 max-w-2xl text-lg">T5 is an open platform. Approved agencies, training providers, employers and community organizations can plug into our marketplace, academy and workforce tools.</p>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 grid sm:grid-cols-3 gap-6">
        {TYPES.map((t, i) => (
          <motion.div key={t.t} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.06 }}
            className="bg-white rounded-2xl p-6 border border-blue-900/5 shadow-sm">
            <span className="grid place-items-center w-12 h-12 rounded-xl bg-teal-50 text-teal-700 mb-4"><t.icon className="w-6 h-6" /></span>
            <h3 className="font-display font-semibold text-blue-950">{t.t}</h3>
            <p className="mt-1.5 text-sm text-slate-500 leading-relaxed">{t.d}</p>
          </motion.div>
        ))}
      </section>

      <section className="max-w-2xl mx-auto px-4 sm:px-6 pb-20">
        <form onSubmit={submit} data-testid="partner-form" className="bg-white rounded-2xl p-8 shadow-lg border border-blue-900/5 space-y-4">
          <h2 className="font-display text-2xl font-bold text-blue-950">Apply to partner</h2>
          <div className="grid sm:grid-cols-2 gap-4">
            <div><Label>Organization</Label><Input required data-testid="partner-org" value={form.organization} onChange={(e) => setForm({ ...form, organization: e.target.value })} className="mt-1.5" /></div>
            <div><Label>Contact name</Label><Input required data-testid="partner-contact" value={form.contact_name} onChange={(e) => setForm({ ...form, contact_name: e.target.value })} className="mt-1.5" /></div>
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div><Label>Email</Label><Input type="email" required data-testid="partner-email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1.5" /></div>
            <div><Label>Phone</Label><Input data-testid="partner-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="mt-1.5" /></div>
          </div>
          <div>
            <Label>Partner type</Label>
            <Select value={form.partner_type} onValueChange={(v) => setForm({ ...form, partner_type: v })}>
              <SelectTrigger data-testid="partner-type" className="mt-1.5"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="agency">Home care agency</SelectItem>
                <SelectItem value="training_provider">Training provider</SelectItem>
                <SelectItem value="employer">Employer</SelectItem>
                <SelectItem value="nonprofit">Community / nonprofit</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div><Label>Tell us about your organization</Label><Textarea rows={4} data-testid="partner-message" value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className="mt-1.5" /></div>
          <Button type="submit" disabled={loading} data-testid="partner-submit" className="w-full h-12 bg-teal-600 hover:bg-teal-700 text-white">{loading ? <Loader2 className="w-5 h-5 animate-spin" /> : "Submit Application"}</Button>
        </form>
      </section>
    </div>
  );
}
